/*
 * Error diagnostics beacon.
 *
 * WHY THIS EXISTS
 * ---------------
 * Most failures in this app end as `} catch { toast.error('Upload failed'); }`
 * -- the user sees a dead-end toast and the error object (with the Firebase
 * code that says WHY) is discarded. Nothing reaches any log: Vercel only sees
 * /api/* calls, and the failing paths are often client-SDK calls straight to
 * Firestore/Storage. Diagnosing a production failure means asking the couple
 * to open their browser console.
 *
 * This module keeps a small in-memory buffer of error events and ships them,
 * fire-and-forget, to /api/diagnostics, which stores them per-wedding with a
 * 30-day TTL. Support can then read the actual error codes instead of
 * guessing from a screenshot.
 *
 * WHAT IT IS NOT
 * --------------
 * Not analytics, not session replay. It records errors only -- no
 * interaction trail, no guest names, no photo bytes, no form content. A
 * couple reading their own "Copy diagnostics" output should find nothing in
 * it they would mind pasting into a support chat.
 *
 * Coverage comes from choke points, not from instrumenting every call site:
 *   - window.onerror / unhandledrejection (crashes, escaped errors)
 *   - a toast.error wrap (anything the app announces as failed, app-wide)
 *   - writeGuard.js (all guarded Firestore writes)
 *   - onSnapshotSafe (listener/read failures)
 *   - explicit logDiag() calls where the error code matters (photo uploads)
 *
 * Every function here must be safe to call from a failure path: nothing may
 * throw, and nothing may toast (a diagnostics error toasting would recurse
 * through the toast wrap).
 */

const BUFFER_MAX = 50;
// Same event repeating within this window bumps a counter instead of
// re-sending -- a reconnect loop on venue Wi-Fi must not turn into hundreds
// of records.
const DEDUPE_WINDOW_MS = 60_000;
const SEND_BATCH_DELAY_MS = 2_000;
const ENDPOINT = '/api/diagnostics';

// Ref codes shown to users on failure toasts ("Ref GW-8F3K"). Unambiguous
// alphabet (no I/L/O/0/1) because couples read these aloud or retype them.
const REF_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

const buffer = [];
const pending = [];
let getUser = () => null;
let installed = false;
let sendTimer = null;

const now = () => Date.now();

const makeRef = () => {
  try {
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    return 'GW-' + Array.from(bytes, (b) => REF_ALPHABET[b % REF_ALPHABET.length]).join('');
  } catch {
    return 'GW-' + Math.random().toString(36).slice(2, 6).toUpperCase();
  }
};

const trunc = (v, n) => {
  const s = v == null ? '' : String(v);
  return s.length > n ? s.slice(0, n) : s;
};

// weddingId from the current URL rather than threading context through every
// page: all wedding-scoped routes carry /wedding/:id/ (plan, live and legacy
// shapes alike). Routes without it (landing, /rsvp/:token, couple-auth)
// buffer locally for "Copy diagnostics" but are not sent -- there is no
// wedding document to file them under.
export const currentWeddingId = () => {
  try {
    const m = window.location.pathname.match(/\/wedding\/([A-Za-z0-9-]{4,64})(?:\/|$)/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
};

const describeError = (err) => {
  if (err == null) return { code: '', message: '' };
  if (typeof err === 'string') return { code: '', message: err };
  return {
    // Firebase errors carry .code ('storage/unauthorized',
    // 'permission-denied'); plain errors carry .name.
    code: err.code || err.name || '',
    message: err.message || String(err),
  };
};

/**
 * Record one error event. Returns the record's ref code (for showing on a
 * toast) -- on a deduped repeat, the ref of the original record.
 *
 * @param {string} event   stable kebab-case name, e.g. 'cover-upload-failed'
 * @param {*}      err     the caught error (Error, Firebase error, string, or null)
 * @param {object} extra   small extra fields; values are stringified and truncated
 */
export function logDiag(event, err, extra = {}) {
  try {
    const { code, message } = describeError(err);
    const dedupeKey = `${event}|${code}|${message}`;
    const last = buffer[buffer.length - 1];
    if (last && last.dedupeKey === dedupeKey && now() - last.ts < DEDUPE_WINDOW_MS) {
      last.count += 1;
      last.ts = now();
      return last.ref;
    }

    const user = (() => { try { return getUser(); } catch { return null; } })();
    const record = {
      ref: makeRef(),
      ts: now(),
      event: trunc(event, 64),
      code: trunc(code, 64),
      message: trunc(message, 500),
      route: trunc(window.location?.pathname || '', 200),
      uid: trunc(user?.uid || '', 128),
      provider: trunc(user?.isAnonymous ? 'anonymous' : (user?.providerData?.[0]?.providerId || (user ? 'unknown' : '')), 32),
      extra: trunc(safeJson(extra), 300),
      count: 1,
      dedupeKey,
    };
    buffer.push(record);
    if (buffer.length > BUFFER_MAX) buffer.shift();
    queueSend(record);
    return record.ref;
  } catch {
    return '';
  }
}

function safeJson(obj) {
  try {
    const s = JSON.stringify(obj);
    return s === '{}' ? '' : s;
  } catch {
    return '';
  }
}

/* ── shipping ─────────────────────────────────────────────────────────── */

function queueSend(record) {
  pending.push(record);
  if (sendTimer) return;
  // Small delay batches a burst (one failure often logs 2-3 events) into a
  // single request. Fire-and-forget: a failed send is dropped, never retried
  // and never logged -- diagnostics must not generate its own traffic storm.
  sendTimer = setTimeout(flush, SEND_BATCH_DELAY_MS);
}

function flush() {
  sendTimer = null;
  const weddingId = currentWeddingId();
  const records = pending.splice(0, pending.length);
  if (!weddingId || records.length === 0) return;
  const payload = JSON.stringify({
    weddingId,
    records: records.slice(0, 10).map(({ dedupeKey, ...r }) => ({
      ...r,
      ua: trunc(navigator.userAgent || '', 200),
      ver: trunc(process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || process.env.REACT_APP_COMMIT_SHA || '', 40),
    })),
  });
  try {
    // sendBeacon survives page unload and never throws on network failure.
    const ok = navigator.sendBeacon?.(ENDPOINT, new Blob([payload], { type: 'application/json' }));
    if (ok) return;
  } catch { /* fall through to fetch */ }
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* offline -- the buffer still feeds Copy diagnostics */ }
}

/* ── install ──────────────────────────────────────────────────────────── */

/**
 * Attach global handlers and the toast.error wrap. Call once at app start.
 *
 * @param {object} opts
 * @param {() => import('firebase/auth').User|null} opts.getUser
 * @param {object} opts.toast  the sonner toast object to wrap (passed in so
 *        this module has no app imports and can never form a cycle)
 */
export function installDiagnostics({ getUser: userGetter, toast } = {}) {
  if (installed) return;
  installed = true;
  if (userGetter) getUser = userGetter;

  try {
    window.addEventListener('error', (e) => {
      logDiag('uncaught-error', e?.error || e?.message);
    });
    window.addEventListener('unhandledrejection', (e) => {
      logDiag('unhandled-rejection', e?.reason);
    });
  } catch { /* non-browser env (tests) */ }

  // Any error toast anywhere in the app becomes a record. Sites that already
  // logged (and put the ref on the toast) are skipped by the ref marker, so
  // one failure is one record, not two.
  if (toast && typeof toast.error === 'function' && !toast.error.__diagWrapped) {
    const original = toast.error.bind(toast);
    const wrapped = (msg, opts) => {
      try {
        if (typeof msg !== 'string' || !msg.includes('· Ref GW-')) {
          logDiag('toast-error', null, { toast: trunc(msg, 200) });
        }
      } catch { /* never block the toast */ }
      return original(msg, opts);
    };
    wrapped.__diagWrapped = true;
    toast.error = wrapped;
  }
}

/* ── Copy diagnostics ─────────────────────────────────────────────────── */

/** The current device's buffered events, newest first. */
export function getRecentRecords() {
  return [...buffer].reverse();
}

/**
 * Human-readable block for the Settings "Copy diagnostics" button. The couple
 * can read everything they are sending before they paste it anywhere.
 */
export function getDiagnosticsText({ weddingId, role } = {}) {
  try {
    const user = (() => { try { return getUser(); } catch { return null; } })();
    const lines = [
      'GuestWho diagnostics',
      `Time: ${new Date().toISOString()}`,
      `Wedding: ${weddingId || currentWeddingId() || 'unknown'}`,
      `Role: ${role || 'unknown'}`,
      `Account: ${user?.uid || 'not signed in'}${user?.isAnonymous ? ' (anonymous)' : ''}`,
      `App version: ${process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || process.env.REACT_APP_COMMIT_SHA || 'unknown'}`,
      `Browser: ${navigator.userAgent}`,
      `Online: ${navigator.onLine}`,
      '',
      buffer.length ? `Recent errors (newest first, this device only):` : 'No errors recorded on this device.',
    ];
    for (const r of getRecentRecords().slice(0, 20)) {
      lines.push(
        `- [${new Date(r.ts).toISOString()}] ${r.ref} ${r.event}` +
        `${r.code ? ` code=${r.code}` : ''}` +
        `${r.count > 1 ? ` (x${r.count})` : ''}` +
        `${r.message ? ` — ${r.message}` : ''}` +
        ` @ ${r.route}`,
      );
    }
    return lines.join('\n');
  } catch {
    return 'GuestWho diagnostics: unavailable';
  }
}
